import { Grid, IconButton, Typography, Box, Tooltip } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { useMemo, useState } from 'react';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { ActionButton } from '../../Button';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import DefaultConstants from '../../../data/Constants';
import { Edit } from '@mui/icons-material';
import useDialog from '../../../hooks/useDialog';
import UpdateObservationsDialog from './UpdateObservationsDialog';
import FormHelper from '../../../helpers/FormHelper';
import PartnerPmsService from '../../../services/PartnerPmsService';
import { GovernanceStatusData, IDefaultStatusData } from '../../../data/Enums';


interface IRoomInformationsDialogProps extends IDefaultDialogProps {
    room: IRoom;
    handleRefreshSelectedRoom: () => void;
    editRoomObservations: boolean;
}


const RoomInformationsDialog: React.FC<IRoomInformationsDialogProps> = ({
    open,
    handleClose,
    room,
    handleRefreshSelectedRoom,
    editRoomObservations
}) => {
    const { translate } = useLanguageContext();
    
    const updateObservationsDialog = useDialog();
    const [expandedObservations, setExpandedObservations] = useState<boolean>(false);


    const observations = useMemo(() => {
        let observations = room.observations;

        if (!observations || observations.trim().length === 0) {
            observations = '-';
        }
        
        if (observations.length <= 100) {
            return <Typography variant='body2'>
                {observations}
            </Typography>;
        }

        return <Typography variant='body2' className='cursor-pointer' onClick={() => setExpandedObservations(!expandedObservations)}>
            {expandedObservations ? observations : `${observations.slice(0, 100)}...`}

            <Typography
                component='p'
                variant='caption'
                className='texttransform-uppercase color-primary'
                marginTop={0.5}
            >
                {translate(expandedObservations ? 'seeLess' : 'seeMore')}
            </Typography>
        </Typography>;
        // eslint-disable-next-line
    }, [room, expandedObservations]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const governanceStatus = useMemo(() => {
        return Object.values(GovernanceStatusData).find(s => s.partnerId === room.governanceStatusId) as IDefaultStatusData;
    }, [room]);
    

    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={`${translate('room')} ${room?.description ?? '...'}`}
                dialogActions={dialogActions}
                className='dialogactions-spacebetween overflow-inherit'
                absolute={true}
                dividers={true}
            >
                <Grid container rowSpacing={DefaultConstants.gridRowSpacing}>
                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('governanceStatus')}
                        </Typography>

                        <Typography
                            variant='body2'
                            className='display-flex alignitems-center' 
                            sx={{ color: governanceStatus.color }}
                        >
                            <governanceStatus.Icon className='inline-icon' />
                            {translate(governanceStatus.name)}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('block')}
                        </Typography>

                        <Typography variant='body2'>
                            {room?.blockDescription ?? '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('floor')}
                        </Typography>

                        <Typography variant='body2'>
                            {room?.floorDescription ?? '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('roomType')}
                        </Typography>

                        <Typography variant='body2'>
                            {room?.roomTypeDescription ?? '-'}
                        </Typography>
                    </Grid>


                    <Grid item xs={12} className='display-flex justifycontent-spacebetween'>
                        <Box>
                            <Typography variant='caption' className='fontsize-10 text-color-2'>
                                {translate('observations')}
                            </Typography>

                            {observations}
                        </Box>

                        {
                            editRoomObservations && <Box paddingTop={1}>
                                <Tooltip title={translate('edit')} placement="left" arrow disableInteractive>
                                    <IconButton onClick={() => updateObservationsDialog.handleOpen()}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </Grid>
                </Grid>
            </Dialog>

            {
                editRoomObservations && <UpdateObservationsDialog
                    open={updateObservationsDialog.open}
                    handleClose={() => updateObservationsDialog.handleClose()}
                    observations={room.observations ?? ''}
                    handleSubmit={(event) => {
                        event.preventDefault();
                
                        let formData = FormHelper.Create(event);
                        formData = FormHelper.Add(formData, 'roomId', room.id);
                        
                        PartnerPmsService.UpdateRoomObservations(formData).then(() => {
                            handleRefreshSelectedRoom();
                            updateObservationsDialog.handleClose();
                        });
                    }}
                />
            }
        </>
    );
}

export default RoomInformationsDialog;